import { useEffect, useState } from "react";
import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { PersonPicker, type PersonOption } from "@/components/people/person-picker";
import { useCreatePdEntity } from "./hooks/useLeads";

type PdEntityType = "deal" | "lead";

export interface LeadCreatePdEntityDialogProps {
  leadId: string;
  tenantId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Additive: fires after the create-entity mutation settles, alongside the
   * hook's own `["leads"]`/`["lead-notes", leadId]` invalidation — see
   * LeadQualifyDialogProps.onSettled for the CCC drill-in's use. */
  onSettled?: () => void;
}

/**
 * Prompt — shown after a note is saved on a Sales lead that isn't in Pipedrive
 * yet — to create a PD deal or lead and pick an owner (or leave unassigned).
 * The lead's notes follow onto the new entity. Owner scoping mirrors
 * LeadAssigneePopover (the lead-management, brand-scoped users pool).
 */
export function LeadCreatePdEntityDialog({
  leadId,
  tenantId,
  open,
  onOpenChange,
  onSettled,
}: LeadCreatePdEntityDialogProps) {
  const [entityType, setEntityType] = useState<PdEntityType>("deal");
  const [assignee, setAssignee] = useState<PersonOption | null>(null);
  const [search, setSearch] = useState("");
  const createEntity = useCreatePdEntity();

  // Clear the type toggle + owner selection whenever the dialog closes, keyed off
  // `open` so it covers EVERY close path — the Cancel button, Escape/overlay, and
  // a programmatic/controlled close from the parent. Resetting only inside the
  // onOpenChange wrapper misses the latter, leaving a prior lead's input to
  // pre-fill the next open. Mirrors DismissLeadsDialog.
  useEffect(() => {
    if (!open) {
      setEntityType("deal");
      setAssignee(null);
      setSearch("");
    }
  }, [open]);

  const handleCreate = async () => {
    if (createEntity.isPending) return;
    try {
      await createEntity.mutateAsync({
        leadId,
        entity_type: entityType,
        assigned_to_user_id: assignee?.id ?? null,
      });
      onOpenChange(false);
    } catch {
      // useCreatePdEntity surfaces a toast on error; keep the dialog open.
    } finally {
      onSettled?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-pd-entity-dialog">
        <DialogHeader>
          <DialogTitle>Create in Pipedrive?</DialogTitle>
          <DialogDescription>
            This lead isn&rsquo;t in Pipedrive yet. Create a deal or lead and assign an
            owner (or leave unassigned). The lead&rsquo;s notes follow onto it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <span className="text-sm font-medium">Type</span>
            <ToggleGroup
              type="single"
              size="sm"
              variant="outline"
              value={entityType}
              onValueChange={(v) => v && setEntityType(v as PdEntityType)}
              className="justify-start"
              aria-label="Pipedrive entity type"
            >
              <ToggleGroupItem value="deal" aria-label="Create a Pipedrive deal" className="px-4">
                Deal
              </ToggleGroupItem>
              <ToggleGroupItem value="lead" aria-label="Create a Pipedrive lead" className="px-4">
                Lead
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Owner</span>
              {assignee ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-xs hover:bg-muted transition-colors"
                  onClick={() => setAssignee(null)}
                  aria-label="Clear owner (leave unassigned)"
                >
                  <span className="truncate max-w-[160px]">{assignee.full_name}</span>
                  <X className="h-3 w-3" />
                </button>
              ) : (
                <span className="text-xs text-muted-foreground">Unassigned</span>
              )}
            </div>
            <div className="rounded-md border">
              <PersonPicker
                source="users"
                pool="lead_owner"
                tenantId={tenantId}
                selected={assignee ? [assignee] : []}
                onToggle={(person) =>
                  setAssignee((cur) => (cur?.id === person.id ? null : person))
                }
                searchQuery={search}
                onSearchChange={setSearch}
                enabled={open}
                placeholder="Search teammates…"
                listClassName="max-h-[200px]"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Leave unassigned to use the brand default owner.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={createEntity.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={createEntity.isPending}
            aria-busy={createEntity.isPending}
          >
            {createEntity.isPending
              ? "Creating…"
              : `Create ${entityType === "deal" ? "deal" : "lead"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
